// src/components/NewsComponents/Carousel.js
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation } from 'swiper/modules';
import { ButtonBase, Card, CardContent, CardMedia, Typography, Box, IconButton, Tooltip } from '@mui/material';
import { Visibility, Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDateSimple } from '../../utils/dateUtils';

const Carousel = ({ content, slidesPerView = 3, spaceBetween = 30, isRecentNews = false, isNewsletterArchive = false }) => {
    const navigate = useNavigate();

    const handleCardClick = (item) => {
        const formatForURL = (str) => {
            const cleanedStr = str
                .replace(/&/g, 'and')
                .replace(/[!']/g, '')
                .replace(/\s+/g, '-')
                .replace(/[,!]/g, '');
            return cleanedStr.toLowerCase();
        };

        const formattedCategory = formatForURL(item.category);
        const formattedTitle = formatForURL(item.title);
        const url = `/news/${formattedCategory}/${formattedTitle}-${item.id}`;
        navigate(url);
    };

    // Handler for newsletter download
    const handleNewsletterDownload = (e, item) => {
        e.preventDefault();
        e.stopPropagation();

        // Add fl_attachment flag to force download
        const downloadUrl = item.pdf.includes('/upload/')
            ? item.pdf.replace('/upload/', '/upload/fl_attachment/')
            : item.pdf;

        // Create temporary link and trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `BEADS_Newsletter_${item.id}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer'; // Correct placement
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Handler for newsletter view (opens in new tab)
    const handleNewsletterView = (e, item) => {
        e.preventDefault();
        e.stopPropagation();

        // Method 1: Simple and modern (recommended)
        const newWindow = window.open(item.pdf, '_blank');
        if (newWindow) {
            newWindow.opener = null; // Security: prevent access to parent window
        }

        // Method 2: Alternative using createElement (more reliable)
        // const link = document.createElement('a');
        // link.href = item.pdf;
        // link.target = '_blank';
        // link.rel = 'noopener noreferrer';
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);
    };

    return (
        <Swiper
            modules={[Navigation]}
            spaceBetween={spaceBetween}
            slidesPerView={slidesPerView}
            navigation
            breakpoints={{
                320: { slidesPerView: 1, spaceBetween: 10 },
                640: { slidesPerView: 2, spaceBetween: 20 },
                1024: { slidesPerView: slidesPerView, spaceBetween: spaceBetween },
            }}
        >
            {content.map((item, index) => {
                const imageSrc = isNewsletterArchive
                    ? item.src
                    : item.cover?.src || 'https://res.cloudinary.com/self-hosted/image/upload/v1707907268/Beads/news/banner_gmfyyr.jpg';
                const imageAlt = isNewsletterArchive ? item.alt : item.cover?.alt || 'beads';

                return (
                    <SwiperSlide key={item.id || `slide-${index}`}>
                        {isNewsletterArchive ? (
                            <Card sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                maxWidth: 345,
                                m: 2,
                                borderRadius: '1rem',
                                overflow: 'hidden',
                                boxShadow: 'none',
                                transition: 'transform .3s, box-shadow .3s',
                                '&:hover': {
                                    transform: 'scale(1.03)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                },
                            }}>
                                <CardMedia
                                    component="img"
                                    image={imageSrc}
                                    alt={imageAlt}
                                    sx={{
                                        height: '80px',
                                        objectFit: 'cover',
                                        borderRadius: '1rem 1rem 0 0'
                                    }}
                                />
                                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{
                                            textAlign: 'left',
                                            marginBottom: '8px',
                                            fontWeight: 'bold',
                                            color: 'blueviolet'
                                        }}
                                    >
                                        {item.date}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ textAlign: 'left', mb: 2 }}
                                    >
                                        {item.description}
                                    </Typography>

                                    {/* Action buttons for newsletter */}
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        justifyContent: 'flex-start',
                                        mt: 'auto'
                                    }}>
                                        <Tooltip title="View PDF" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleNewsletterView(e, item)}
                                                sx={{
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.dark',
                                                    },
                                                    padding: '8px',
                                                }}
                                                aria-label={`View ${item.description}`}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Download PDF" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleNewsletterDownload(e, item)}
                                                sx={{
                                                    backgroundColor: 'secondary.main',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: 'secondary.dark',
                                                    },
                                                    padding: '8px',
                                                }}
                                                aria-label={`Download ${item.description}`}
                                            >
                                                <Download fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </CardContent>
                            </Card>
                        ) : (
                            <ButtonBase
                                onClick={() => handleCardClick(item)}
                                sx={{ display: 'block', width: '100%' }}
                            >
                                <Card sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxWidth: isRecentNews ? 'none' : 345,
                                    width: isRecentNews ? 'auto' : 345,
                                    m: 2,
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    boxShadow: 'none',
                                    transition: 'transform .3s',
                                    '&:hover': {
                                        transform: 'scale(1.03)',
                                    },
                                }}>
                                    <CardMedia
                                        component="img"
                                        image={imageSrc}
                                        alt={imageAlt}
                                        sx={{
                                            height: '200px',
                                            objectFit: 'cover',
                                            borderRadius: '1rem',
                                        }}
                                    />
                                    <CardContent sx={{
                                        flexGrow: 1,
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ textAlign: 'left', marginBottom: '8px' }}
                                        >
                                            {item.category}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{
                                                fontWeight: 'bold',
                                                '&:hover': { color: '#f39c12' },
                                                textAlign: 'left'
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ textAlign: 'left', my: 1 }}
                                            >
                                                {formatDateSimple(item.date)}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ textAlign: 'left' }}
                                            >
                                                Published by: {item.author}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </ButtonBase>
                        )}
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
};

export default Carousel;